import { Headline2 } from '@/app/components/Typography'
import { ICourse } from '@/types/course'
import { CourseVideo } from './CourseVideo'
import { useCourseCategoryImage } from '@/app/hooks/useCourseCategoryImage'
import { ICategory } from '@/types/category'
import { useTranslations } from 'next-intl'
import { ImageWithFallback } from '../../shared/components/image-with-fallback'

type CourseMotivationProps = {
  courseInfo: Pick<
    ICourse,
    'courseMotivation' | 'categories' | '_id' | 'educationProvider' | 'imageUrl'
  >
} & React.HTMLAttributes<HTMLDivElement>

export function CourseMotivation({
  courseInfo,
  ...props
}: CourseMotivationProps) {
  const t = useTranslations('CoursePage')
  const { courseMotivation } = courseInfo || {}

  const { categoryImage, category } = useCourseCategoryImage(courseInfo)

  if (!courseMotivation) {
    return null
  }

  const { videoUrl, motivation, advisor } = courseMotivation

  return (
    <div
      {...props}
      className={`relative flex flex-col gap-8 py-6 ${props.className}`}
    >
      <Headline2 className="text-center">{t('why_take_this_course')}</Headline2>
      <div
        className={`flex flex-col gap-8 md:flex-row ${videoUrl ? 'md:items-center' : 'md:items-start'}`}
      >
        {videoUrl ? (
          <CourseVideo className="md:w-[50%]" videoUrl={videoUrl}></CourseVideo>
        ) : (
          <ImageWithFallback
            className="md:w-[50%] object-cover"
            {...categoryImage}
            src={courseInfo.imageUrl ?? categoryImage?.src}
            fallbackSrc={categoryImage?.src}
            alt={`${(category as ICategory)?.name}`}
          />
        )}
        <div className="flex flex-col gap-2 md:order-first md:w-[50%]">
          {advisor && (
            <div className="flex items-center gap-3">
              {advisor.avatarUrl && (
                <ImageWithFallback
                  src={advisor.avatarUrl}
                  alt={t('advisor')}
                  width={40}
                  height={40}
                  className={`rounded-full w-10 h-10`}
                />
              )}
              <div className="">
                <p className="text-sm font-semibold">{advisor.name}</p>
                {advisor.role && (
                  <p className="text-sm text-mydra-black">{advisor.role}</p>
                )}
              </div>
            </div>
          )}
          {motivation && (
            <p className="font-sans text-sm leading-6 text-mydra-black">
              {motivation}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
